import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack,
  Save
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import {
  getJobOpenings,
  createJob,
  updateJob,
  JobOpening
} from '@/services/DashboardAnalyticsService';

const employmentTypes = [
  { value: 'full-time', label: 'Full Time' },
  { value: 'part-time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'intern', label: 'Intern' }
];

const jobStatuses = [
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'on-hold', label: 'On Hold' }
];

const locationOptions = [
  { value: 'Remote', label: 'Remote' },
  { value: 'Hybrid - Bengaluru', label: 'Hybrid - Bengaluru' },
  { value: 'Hybrid - Mumbai', label: 'Hybrid - Mumbai' },
  { value: 'Hybrid - Delhi', label: 'Hybrid - Delhi' },
  { value: 'On-site - Bengaluru', label: 'On-site - Bengaluru' },
  { value: 'On-site - Mumbai', label: 'On-site - Mumbai' },
  { value: 'On-site - Delhi', label: 'On-site - Delhi' }
];

type JobStatus = 'draft' | 'open' | 'closed' | 'on-hold';

interface JobFormData {
  title: string;
  department: string;
  description: string;
  requirements: string;
  employment_type: string;
  location: string;
  positions_count: number;
  salary_min: number | '';
  salary_max: number | '';
  status: JobStatus;
}

const initialFormData: JobFormData = {
  title: '',
  department: '',
  description: '',
  requirements: '',
  employment_type: 'full-time',
  location: '',
  positions_count: 1,
  salary_min: '',
  salary_max: '',
  status: 'draft'
};

const JobPostingFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<JobFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch job data if editing
  useEffect(() => {
    if (isEditMode && id) {
      const fetchJob = async () => {
        setLoading(true);
        try {
          const response = await getJobOpenings(1, 100);
          if (response.success && response.data) {
            const job = response.data.find((j: JobOpening) => j.id === Number(id));
            if (job) {
              setFormData({
                title: job.title,
                department: job.department || '',
                description: job.description || '',
                requirements: job.requirements || '',
                employment_type: job.employment_type || 'full-time',
                location: job.location || '',
                positions_count: job.positions_count || 1,
                salary_min: job.salary_min || '',
                salary_max: job.salary_max || '',
                status: job.status || 'draft'
              });
            } else {
              setError('Job not found');
            }
          }
        } catch (err: any) {
          setError(err.message || 'Failed to load job');
        } finally {
          setLoading(false);
        }
      };
      fetchJob();
    }
  }, [id, isEditMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'positions_count' || name === 'salary_min' || name === 'salary_max'
        ? (value === '' ? '' : Number(value))
        : value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === 'status') {
      setFormData(prev => ({ ...prev, status: value as JobStatus }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const jobData = {
        ...formData,
        salary_min: formData.salary_min === '' ? undefined : formData.salary_min,
        salary_max: formData.salary_max === '' ? undefined : formData.salary_max
      };

      let response;
      if (isEditMode && id) {
        response = await updateJob(Number(id), jobData);
      } else {
        response = await createJob(jobData);
      }

      if (response.success) {
        navigate('/hr/jobs');
      } else {
        setError(response.error?.message || 'Failed to save job');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="md">
        <Box sx={{ py: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/hr/jobs')}
              sx={{ textTransform: 'none' }}
            >
              Back to Jobs
            </Button>
          </Box>

          <Card sx={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                {isEditMode ? 'Edit Job Posting' : 'Create New Job Posting'}
              </Typography>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  name="title"
                  label="Job Title"
                  value={formData.title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  placeholder="e.g., Senior Software Engineer"
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    name="department"
                    label="Department"
                    value={formData.department}
                    onChange={handleInputChange}
                    fullWidth
                    placeholder="e.g., Engineering"
                  />
                  <FormControl fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={formData.location}
                      label="Location"
                      onChange={(e) => handleSelectChange('location', e.target.value)}
                    >
                      {locationOptions.map((loc) => (
                        <MenuItem key={loc.value} value={loc.value}>
                          {loc.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Employment Type</InputLabel>
                    <Select
                      value={formData.employment_type}
                      label="Employment Type"
                      onChange={(e) => handleSelectChange('employment_type', e.target.value)}
                    >
                      {employmentTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => handleSelectChange('status', e.target.value)}
                    >
                      {jobStatuses.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                  <TextField
                    name="positions_count"
                    label="Number of Positions"
                    type="number"
                    value={formData.positions_count}
                    onChange={handleInputChange}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                  <TextField
                    name="salary_min"
                    label="Salary Min (INR)"
                    type="number"
                    value={formData.salary_min}
                    onChange={handleInputChange}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>
                    }}
                  />
                  <TextField
                    name="salary_max"
                    label="Salary Max (INR)"
                    type="number"
                    value={formData.salary_max}
                    onChange={handleInputChange}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>
                    }}
                  />
                </Box>

                <TextField
                  name="description"
                  label="Job Description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="Describe the role, responsibilities, and what the candidate will be doing..."
                />

                <TextField
                  name="requirements"
                  label="Requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="List the required skills, experience, and qualifications..."
                />

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/hr/jobs')}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                  >
                    {submitting ? 'Saving...' : isEditMode ? 'Update Job' : 'Create Job'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Layout>
  );
};

export default JobPostingFormPage;
